import React, { useState, useRef, useEffect } from "react";

import {
  Container,
  Table,
  DownloadButton,
  Header,
  UploadContent,
  UploadSide,
  ModelSide,
  TableColumns,
  TableList,
  TableItem,
  ActionsContent,
  CancelButton,
  ConfirmButton,
  InputContainer,
  Input,
  HistoryContent,
  Content,
  TableHeader,
  TextInput,
} from "./styles";

//Firebase and context
import { getStorage, ref, getDownloadURL } from "firebase/storage";

//Custom components
import ProfileButton from "@components/ProfileButton/ProfileButton";

//Icons
import importIcon from "@assets/importIcon.svg";
import importIconActive from "@assets/importIconActive.svg";
import { FiDownload } from "react-icons/fi";
import cpfIcon from "@assets/cpfIcon.svg";

//Utils
import { toast } from "react-toastify";
import { alertErrorStyle } from "@resources/alertErrorStyle";
import { csvHelper } from "@helpers/csvHelper";
import { arrayHelper } from "@helpers/array";
import Select from "react-select";
import { ClipLoader } from "react-spinners";
import HistoryList from "./components/HistoryList/HistoryList";
import { RemoveClientValidation } from "@domain/models/RemoveClientValidation";
import { RemoveClientsUseCase, ValidateClientRemoveUseCase } from "@useCases/index";
import { HttpRemoveRepository } from "@data/repositories/index";
import { api } from "@data/api";
import { RemoveClient } from "@domain/models/RemoveClient";
import { ProgressBar } from "@components/ProgressBar/ProgressBar";
import { Report, ReportResult, reportResultOptions } from "@domain/models/Report";
import { reportResultSelectOptions } from "@constants/reportResultSelectOptions";
import useRemoveHistory from "@hooks/useRemoveHistory";
import { RemoveHistory } from "@domain/models/RemoveHistory";
import { JobProgressBar } from "@components/JobProgressBar/JobProgressBar";
import useRemoveJob from "@hooks/useRemoveJob";

let resultOptions = reportResultSelectOptions;

const validOptions: ReportResult[] = [
  reportResultOptions.SIGNED,
  reportResultOptions.HAVE_LAWYER,
  reportResultOptions.NO_RIGHT,
  reportResultOptions.NOT_INTERESSED,
];

resultOptions = reportResultSelectOptions.filter((x) => validOptions.includes(x.value));

const RemovePage = () => {
  const { getRemoveHistory } = useRemoveHistory();
  const { removeJob, watchPendingRemoveJob, cleanRemoveJob } = useRemoveJob();

  const inputRef = useRef();

  const [removeHistory, setRemoveHistory] = useState<RemoveHistory[]>([]);

  const [processLoading, setProcessLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const [selectedResult, setSelectedResult] = useState<Report["result"]>("Contrato fechado");
  const [description, setDescription] = useState("");

  const [processedData, setProcessedData] = useState<RemoveClientValidation>();

  useEffect(() => {
    fetchRemoveHistory();
  }, []);

  useEffect(() => {
    if (removeJob?.status === "completed") {
      fetchRemoveHistory();
    }
  }, [removeJob]);

  const fetchRemoveHistory = async () => {
    return getRemoveHistory().then((data) => {
      setRemoveHistory(data);
    });
  };

  const clearOperation = () => {
    setInputValue("");
    setProcessedData(null);
  };

  const readCsvFile = (csvFile: File) => {
    const reader = new FileReader();

    setProcessLoading(true);

    reader.onload = (e) => {
      const text = e.target.result as string;

      const splitFile = text.split("\r\n");
      const [header, ...files] = splitFile;

      const headerSplit = header.split(";");

      const { valid, invalidFields } = csvHelper.validateRemoveHeader(headerSplit);

      if (!valid) {
        return toast(
          `A planilha selecionada não possui as colunas obrigatórias: ${arrayHelper.arrayToString(
            invalidFields,
            ", "
          )}`,
          alertErrorStyle
        );
      }

      const { array, errors } = csvHelper.processRemoveCsv(files, headerSplit);

      if (errors.length === 0) {
        // this.props.setData(selectedOption, array);
        processData(array);
      } else {
        for (const errorMessage of errors) {
          toast(errorMessage, alertErrorStyle);
        }
      }
    };

    reader.readAsText(csvFile);
  };

  const processData = (data: RemoveClient[]) => {
    const useCase = new ValidateClientRemoveUseCase(new HttpRemoveRepository(api));

    useCase
      .execute(data)
      .then((removeClientValidation) => {
        setProcessedData(removeClientValidation);
      })
      .finally(() => {
        setProcessLoading(false);
      });
  };

  const handleRemove = async () => {
    setLoading(true);

    const useCase = new RemoveClientsUseCase(new HttpRemoveRepository(api));

    useCase
      .execute({
        result: selectedResult,
        description,
        selectedClients: processedData.validClients,
        notSelectedClients: processedData.invalidClients,
      })
      .then((job) => {
        watchPendingRemoveJob(job.id);
      })
      .finally(() => {
        clearOperation();
        setLoading(false);
      });
  };

  const downloadModelos = async (name) => {
    const storage = getStorage();
    const pathReference = ref(
      storage,
      `gs://${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/${name}.csv`
    );

    getDownloadURL(pathReference)
      .then((url) => {
        // `url` is the download URL for 'images/stars.jpg'
        window.open(url, "_blank", "noopener,noreferrer");
      })
      .catch(() => {
        alert("Ocorreu um erro");
      });
  };

  const customStyle = {
    container: (base) => ({
      ...base,
      borderRadius: 40,
      marginBottom: 10,
    }),
    control: (base) => ({
      ...base,
      padding: 3,
      borderRadius: 40,
    }),
    multiValue: (base) => ({
      ...base,
      borderRadius: 40,
      backgroundColor: "#ECF2FB",
    }),
  };

  const handleSelect = async (newValue) => {
    setSelectedResult(newValue.value);
  };

  return (
    <Container>
      <JobProgressBar
        job={removeJob}
        type="remove"
        onClose={() => {
          cleanRemoveJob();
        }}
      />
      <Header>
        <h1>Remover clientes do mapa</h1>
        <ProfileButton arrowColor="var(grey2)" />
      </Header>

      <Content>
        <UploadContent>
          <UploadSide>
            <h3>Os clientes serão removidos do mapa através de um atendimento</h3>

            <h4>Selectione um resultado para o atendimento</h4>
            <Select
              key="result"
              options={resultOptions}
              defaultValue={{ label: selectedResult, value: selectedResult }}
              styles={customStyle}
              onChange={handleSelect}
            />

            <h4>Descrição</h4>
            <TextInput>
              <input
                name="description"
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Descrição do atendimento"
              />
            </TextInput>

            <InputContainer>
              <div>
                <img src={processedData ? importIconActive : importIcon} alt="img img" />
                <h3>{processedData ? "Arquivo selecionado" : "Escolher arquivo..."}</h3>
              </div>

              <Input
                type="file"
                accept=".csv"
                id="csvfile"
                ref={inputRef}
                placeholder=""
                value={inputValue}
                onChange={async (e) => {
                  setInputValue(e.target.value);
                  readCsvFile(e.target.files[0]);
                }}
              />
            </InputContainer>
            {inputValue && <ProgressBar success={!processLoading} />}
          </UploadSide>
          <ModelSide>
            <DownloadButton onClick={async () => downloadModelos("remocao")}>
              <FiDownload />
              Modelo Remoção do Mapa
            </DownloadButton>
          </ModelSide>
        </UploadContent>
        {processedData && (
          <Table>
            <TableHeader>
              {processedData.validClients.length > 0 ? (
                <>
                  <h1>{processedData.validClients.length} clientes serão removidos do mapa</h1>
                  {processedData && processedData.invalidClients?.length > 0 && (
                    <h3>({processedData.invalidClients.length} clientes não estão no mapa)</h3>
                  )}
                </>
              ) : (
                <h1>Nenhum cliente selecionado está no mapa</h1>
              )}
            </TableHeader>

            {processedData && processedData.validClients.length > 0 && (
              <>
                <TableColumns>
                  <h3>CPF</h3>
                  <h3>Nome</h3>
                  <h3>Processos</h3>
                </TableColumns>
                <TableList>
                  {processedData.validClients.map((item, index) => (
                    <TableItem key={index}>
                      <div>
                        <img src={cpfIcon} alt="img img" />
                        <h3>{item.id}</h3>
                      </div>
                      <h1>{item.name}</h1>
                      <h1>{item.processes}</h1>
                    </TableItem>
                  ))}
                </TableList>
              </>
            )}
            {selectedResult && description ? (
              <ActionsContent>
                {loading ? (
                  <ClipLoader color="var(--red)" />
                ) : (
                  <>
                    <CancelButton onClick={() => clearOperation()}>
                      <p>Cancelar remoção</p>
                    </CancelButton>
                    {processedData.validClients.length > 0 && (
                      <ConfirmButton
                        disabled={false}
                        onClick={(e) => {
                          e.preventDefault();
                          handleRemove();
                        }}
                      >
                        <p>Remover do mapa</p>
                      </ConfirmButton>
                    )}
                  </>
                )}
              </ActionsContent>
            ) : (
              <ActionsContent>
                {processedData && processedData.validClients.length > 0 ? (
                  <h3>Selecione o resultado e descreva o atendimento para continuar</h3>
                ) : (
                  <CancelButton onClick={() => clearOperation()}>
                    <p>Cancelar remoção</p>
                  </CancelButton>
                )}
              </ActionsContent>
            )}
          </Table>
        )}
      </Content>

      <HistoryContent>
        <HistoryList
          removeHistory={removeHistory}
          onUndoRemove={(job) => watchPendingRemoveJob(job.id)}
        />
      </HistoryContent>
    </Container>
  );
};

export default RemovePage;
